/**
 * Interruptor para marcação booleana da questão.
 *
 * É um `<input type="checkbox">` de verdade, visualmente escondido dentro de um
 * `<label>`, e não um `<button role="switch">`: mantém teclado, leitor de tela e
 * o `checked` nativo sem reimplementar nada — o que muda é só o desenho. Não
 * existe `Switch` no design-system, e as marcações da questão eram o único
 * consumidor; quando aparecer um segundo, sobe para lá.
 *
 * O rótulo inteiro é a área de clique: o trilho tem 36px e, varrendo uma prova
 * de 80 questões, alvo pequeno é erro de clique.
 */
export function Switch({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label
      title={hint}
      className={[
        // `relative` NÃO é enfeite: `sr-only` posiciona o input em `absolute`, e
        // sem um contentor posicionado aqui o bloco contentor dele vira o
        // `<body>` — fora do `main` do `AppLayout`, que é quem rola. O input
        // passa a morar na coordenada do DOCUMENTO equivalente ao scroll de
        // dentro do `main` (medido: 5541px numa prova de 80 questões), e o
        // `overflow-hidden` do shell não o clipa, porque `overflow` só clipa
        // descendente `absolute` se o próprio elemento for posicionado.
        // Resultado: clicar no rótulo dá foco a esse input, o navegador rola o
        // DOCUMENTO até ele, e o shell (`h-screen`, no topo) sai da tela — a
        // "tela branca" ao marcar uma questão. Com `relative`, o contentor é o
        // rótulo, o input fica onde está e o clique não rola nada.
        'relative flex items-center gap-2 text-sm',
        disabled
          ? 'text-(--color-text-secondary) cursor-not-allowed opacity-60'
          : 'text-(--color-text-primary) cursor-pointer',
      ].join(' ')}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {/*
        Trilho e botão são pintados pelo `checked` do React, e não por
        `peer-checked:`: a variante `peer-*` compila para `~` (irmão), e o botão
        é DESCENDENTE do trilho — a regra não o alcançaria. O que fica no `peer`
        é só o anel de foco, que incide no trilho (esse sim, irmão do input) e
        devolve o foco visível que o `sr-only` tirou.
      */}
      <span
        aria-hidden
        className={[
          'peer-focus-visible:ring-(--color-accent) relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2',
          checked ? 'bg-(--color-accent)' : 'bg-(--color-border)',
        ].join(' ')}
      >
        <span
          className={[
            'absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </span>
      {label}
    </label>
  );
}
